import React, { useState } from "react";
import { Box, IconButton, Menu, MenuItem, Tooltip, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { FontFamily } from "../../component/theme/fontFamily";
import { fontWeight } from "../../component/theme/fontWeight";
import { fontSize } from "../../component/theme/fontSize";
import { ThemeColor } from "../../component/theme/color";

/**
 * How many trailing folders stay on screen. The current folder plus its parent
 * is enough to know where you are; anything before that goes behind the "..."
 * menu. Deep trees used to wrap onto three lines and shove the toolbar around.
 */
const TAIL_CRUMBS = 2;

const Separator = () => (
  <Typography
    aria-hidden
    sx={{
      px: 0.75,
      flexShrink: 0,
      color: ThemeColor.GREY,
      fontFamily: FontFamily.Poppins,
      fontSize: fontSize.h4.medium,
      lineHeight: 1,
      userSelect: "none",
    }}
  >
    /
  </Typography>
);

/**
 * Single-line folder path.
 *
 * `stack` is [{ id, name }] from the root downwards. `onNavigate(crumb, index)`
 * fires for every crumb except the last one, which is where you already are.
 */
const FolderBreadcrumbs = ({
  stack = [],
  onNavigate,
  onBack,
  onRootClick,
  rootLabel = "My Folder",
}) => {
  const [menuAnchor, setMenuAnchor] = useState(null);

  // Keep the tail; everything before it collapses into the menu.
  const hidden = stack.length > TAIL_CRUMBS ? stack.slice(0, -TAIL_CRUMBS) : [];
  const visible = hidden.length ? stack.slice(-TAIL_CRUMBS) : stack;
  const fullPath = [rootLabel, ...stack.map((crumb) => crumb.name)].join(" / ");

  const goTo = (crumb, index) => {
    setMenuAnchor(null);
    if (index < stack.length - 1 && onNavigate) onNavigate(crumb, index);
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        flexWrap: "nowrap",
        minWidth: 0,
        width: "100%",
      }}
    >
      {onBack && (
        <IconButton size="small" onClick={onBack} sx={{ flexShrink: 0, mr: 0.5 }}>
          <ArrowBackIcon fontSize="small" />
        </IconButton>
      )}

      <Tooltip title={stack.length ? fullPath : ""} placement="bottom-start">
        <Typography
          onClick={onRootClick}
          fontFamily={FontFamily.Poppins}
          fontWeight={fontWeight.W600}
          sx={{
            flexShrink: 0,
            whiteSpace: "nowrap",
            fontSize: { xs: fontSize.h3.medium, sm: fontSize.h2.small },
            cursor: onRootClick ? "pointer" : "default",
            "&:hover": { textDecoration: onRootClick ? "underline" : "none" },
          }}
        >
          {rootLabel}
        </Typography>
      </Tooltip>

      {hidden.length > 0 && (
        <>
          <Separator />
          <Tooltip title={`${hidden.length} more folder${hidden.length > 1 ? "s" : ""}`}>
            <IconButton
              size="small"
              onClick={(e) => setMenuAnchor(e.currentTarget)}
              sx={{
                flexShrink: 0,
                p: 0.25,
                borderRadius: "6px",
                color: ThemeColor.GREY,
                "&:hover": { backgroundColor: "rgba(0,0,0,0.06)" },
              }}
            >
              <MoreHorizIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
          >
            {hidden.map((crumb, index) => (
              <MenuItem
                key={crumb.id || index}
                onClick={() => goTo(crumb, index)}
                sx={{
                  fontFamily: FontFamily.Poppins,
                  fontSize: fontSize.h4.large,
                  // Nesting depth, so the menu reads as a path and not a flat list.
                  pl: 2 + index * 1.25,
                  maxWidth: 360,
                }}
              >
                <Box
                  component="span"
                  sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                >
                  {crumb.name}
                </Box>
              </MenuItem>
            ))}
          </Menu>
        </>
      )}

      {visible.map((crumb, visibleIndex) => {
        const index = hidden.length + visibleIndex;
        const isLast = index === stack.length - 1;

        return (
          <React.Fragment key={crumb.id || index}>
            <Separator />
            <Tooltip title={crumb.name} placement="bottom">
              <Typography
                onClick={() => goTo(crumb, index)}
                fontFamily={FontFamily.Poppins}
                fontWeight={isLast ? fontWeight.W600 : fontWeight.W400}
                color={isLast ? "inherit" : ThemeColor.GREY}
                sx={{
                  // The current folder gets whatever room is left; its parent
                  // shrinks first so the name you are looking at stays readable.
                  flexShrink: isLast ? 0 : 1,
                  minWidth: isLast ? "auto" : 40,
                  maxWidth: isLast ? "none" : { xs: 90, sm: 160, md: 200 },
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  fontSize: isLast
                    ? { xs: fontSize.h3.medium, sm: fontSize.h2.small }
                    : fontSize.h3.medium,
                  cursor: isLast ? "default" : "pointer",
                  "&:hover": { textDecoration: isLast ? "none" : "underline" },
                }}
              >
                {crumb.name}
              </Typography>
            </Tooltip>
          </React.Fragment>
        );
      })}
    </Box>
  );
};

export default FolderBreadcrumbs;
